/**
 * Calendar utility functions for computing grid data and date operations.
 * All functions are pure with no side effects.
 */

export interface DisplayedMonth {
  year: number; // e.g., 2025
  month: number; // 0-indexed (0 = January, 11 = December)
}

/** Returns true if a date falls within the given month/year. */
export const isInMonth = (date: Date, year: number, month: number): boolean => {
  return date.getFullYear() === year && date.getMonth() === month;
};

/** Returns the first day of the given month. */
export const getFirstDayOfMonth = (year: number, month: number): Date => {
  return new Date(year, month, 1);
};

/**
 * Returns a 2D array of Date objects representing the calendar grid for a given month.
 * Each row is a week (Sunday through Saturday), and the grid contains 4–6 rows.
 *
 * Algorithm:
 * 1. Find the first day of the displayed month
 * 2. Find the Sunday on or before that first day (row 0, column 0)
 * 3. Fill 6 rows × 7 columns with consecutive dates starting from that Sunday
 * 4. Trim trailing rows that contain no days from the displayed month
 */
export const getCalendarGrid = (year: number, month: number): Date[][] => {
  const firstDay = getFirstDayOfMonth(year, month);
  // Find the Sunday on or before the first day of the month
  const startOffset = firstDay.getDay(); // 0 = Sunday, so this is the number of days to go back
  const gridStart = new Date(year, month, 1 - startOffset);

  // Fill 6 rows × 7 columns
  const rows: Date[][] = [];
  for (let row = 0; row < 6; row++) {
    const week: Date[] = [];
    for (let col = 0; col < 7; col++) {
      const dayOffset = row * 7 + col;
      week.push(new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + dayOffset));
    }
    rows.push(week);
  }

  // Trim trailing rows that contain no days from the displayed month
  while (rows.length > 4) {
    const lastRow = rows[rows.length - 1];
    const hasCurrentMonthDay = lastRow.some((date) => isInMonth(date, year, month));
    if (!hasCurrentMonthDay) {
      rows.pop();
    } else {
      break;
    }
  }

  return rows;
};

/** Returns true if two dates represent the same calendar day. */
export const isSameDay = (a: Date, b: Date): boolean => {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

/** Validates whether a value is a valid Date object. */
export const isValidDate = (date: unknown): date is Date => {
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * Formats a Date to 'yyyy-MM-dd' string for map lookups and lexicographic comparison.
 */
export const formatDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
